import React from 'react';
import { Link } from 'react-router-dom';
import { Text } from '../../../common/components';
import routes from '../../../common/constants/routes';
import {
  DiffItem,
  ValueDiffItem,
  ListCollectionDiffItem,
  BuildingLogActionItem,
  PlombaDiffItem,
  getArrayItemType,
  formatValueForDisplay,
  formatObject
} from './BuildingLogDataParser';
import { formatDate } from '../../../common/utils/Date/Date';

// Typy pro entity odkazy
interface Unit {
  id: string;
  label: string;
}

interface Profile {
  id: string;
  company?: string;
  firstName?: string;
  lastName?: string;
}

// Renderování odkazů na jednotky
export const renderUnitLinks = (units: Unit[] | undefined): React.ReactNode => {
  if (!units || units.length === 0) return null;

  return units.map((unit, index) => (
    <span key={unit.id}>
      <Link
        to={`${routes.units}unit/${unit.id}`}
        className="BuildingLogModal__unitLink"
        target='_blank'
      >
        {unit.label}
      </Link>
      {index < units.length - 1 && ', '}
    </span>
  ));
};

// Renderování odkazů na profily
export const renderProfileLinks = (profiles: Profile[] | undefined): React.ReactNode => {
  if (!profiles || profiles.length === 0) return null;

  return profiles.map((profile, index) => (
    <span key={profile.id}>
      <Link
        to={`${routes.units}profile/${profile.id}`}
        className="BuildingLogModal__unitLink"
        target='_blank'
      >
        {profile.company || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.id}
      </Link>
      {index < profiles.length - 1 && ', '}
    </span>
  ));
};

// Renderování value diff položky
const renderValueDiffItem = (item: ValueDiffItem, index: number): React.ReactNode => (
  <tr key={`value-${index}`} className='BuildingLogModal__diffTable__row'>
    <td><Text size="body-small" weight="700" align='left'>{item.label}</Text></td>
    <td><Text size="body-medium" align='left'>{formatValueForDisplay(item.previous)}</Text></td>
    <td><Text size="body-medium" align='left'>{formatValueForDisplay(item.current)}</Text></td>
  </tr>
);

// Renderování list/collection diff položky
const renderListCollectionDiffItem = (item: ListCollectionDiffItem, index: number): React.ReactNode => {
  const addedType = getArrayItemType(item.added);
  const removedType = getArrayItemType(item.removed);
  
  const formatItemsAsElements = (items: string[] | Record<string, unknown>[], type: 'string' | 'object'): React.ReactNode[] => {
    if (items.length === 0) return [];
    
    if (type === 'string') {
      return (items as string[]).map((item: string, idx: number) => (
        <Text key={idx} size="body-medium" className='BuildingLogModal__listItem'>
          • {item}
        </Text>
      ));
    }
    
    // Pro objekty - použijeme specifické formátování podle kontextu (label)
    return (items as Record<string, unknown>[]).map((itemObj: Record<string, unknown>, idx: number) => (
      <Text key={idx} size="body-medium" className='BuildingLogModal__listItem'>
        • {formatObject(itemObj, item.label)}
      </Text>
    ));
  };

  const addedElements = formatItemsAsElements(item.added, addedType);
  const removedElements = formatItemsAsElements(item.removed, removedType);

  return (
    <div key={`list-collection-${index}`} className='BuildingLogModal__listSection mb-2'>
      <Text size="h3" className='mb-1'>
        {item.label || 'Seznam'}
      </Text>
      
      {addedElements.length > 0 && (
        <div className='BuildingLogModal__listSection__group mb-1'>
          <Text size="body-small" weight="600" color="Neutral700" className='mb-1'>
            Přidáno:
          </Text>
          <div className='BuildingLogModal__listSection__items ml-1'>
            {addedElements}
          </div>
        </div>
      )}
      
      {removedElements.length > 0 && (
        <div className='BuildingLogModal__listSection__group mb-1'>
          <Text size="body-small" weight="600" color="Neutral700" className='mb-1'>
            Odebráno:
          </Text>
          <div className='BuildingLogModal__listSection__items ml-1'>
            {removedElements}
          </div>
        </div>
      )}
    </div>
  );
};

// Bezpečné formátování data z API (ISO string) — při nevalidní hodnotě vrátí null
const formatPlombaDate = (value?: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : formatDate(date);
};

// Renderování plomby (probíhající/ukončené řízení v katastru)
const renderPlombaItem = (item: PlombaDiffItem, index: number): React.ReactNode => {
  const prijato = formatPlombaDate(item.datumPrijeti);
  const operace = (item.operace || []).filter(op => op.nazev);

  return (
    <div key={`plomba-${index}`} className='BuildingLogModal__listSection mb-2'>
      <Text size="h3" className='mb-1'>
        {item.ukonceno ? 'Ukončené řízení' : 'Probíhající řízení'}
        {item.cisloRizeni ? ` ${item.cisloRizeni}` : ''}
      </Text>

      {item.typNazev && (
        <Text size="body-medium" className='BuildingLogModal__listItem'>
          Typ řízení: <strong>{item.typNazev}</strong>
        </Text>
      )}
      {prijato && (
        <Text size="body-medium" className='BuildingLogModal__listItem'>
          Přijato: {prijato}
        </Text>
      )}
      {item.stav && (
        <Text size="body-medium" className='BuildingLogModal__listItem'>
          Stav řízení: {item.stav}
        </Text>
      )}

      {operace.length > 0 && (
        <div className='BuildingLogModal__listSection__group mb-1 mt-1'>
          <Text size="body-small" weight="600" color="Neutral700" className='mb-1'>
            Provedené operace:
          </Text>
          <div className='BuildingLogModal__listSection__items ml-1'>
            {operace.map((op, idx) => {
              const datum = formatPlombaDate(op.datum);
              return (
                <Text key={idx} size="body-medium" className='BuildingLogModal__listItem'>
                  • {op.nazev}{datum ? ` (${datum})` : ''}
                </Text>
              );
            })}
          </div>
        </div>
      )}

      {!item.cisloRizeni && item.id !== undefined && (
        <Text size="body-small" color="Neutral600">
          Interní číslo řízení v katastru: {item.id}
        </Text>
      )}
    </div>
  );
};

// Renderování action položky s inline odkazy
const renderActionItem = (item: BuildingLogActionItem, index: number): React.ReactNode => {
  const renderActionWithLinks = (): React.ReactNode => {
    switch (item.actionType) {
      case 'addUnit':
        return (
          <>
            Přidána nová jednotka {item.unitId && item.unit ? (
              <Link
                to={`${routes.units}unit/${item.unitId}`}
                className="BuildingLogModal__unitLink"
                target='_blank'
              >
                {item.unit}
              </Link>
            ) : (
              item.unit || 'neznámá'
            )}
          </>
        );
      case 'removeUnit':
        return (
          <>
            Odebrána jednotka {item.unitId && item.unit ? (
              <Link
                to={`${routes.units}unit/${item.unitId}`}
                className="BuildingLogModal__unitLink"
                target='_blank'
              >
                {item.unit}
              </Link>
            ) : (
              item.unit || 'neznámá'
            )}
          </>
        );
      case 'addProfile':
        return (
          <>
            Přidán nový vlastník {item.profileId && item.profileName ? (
              <Link
                to={`${routes.units}profile/${item.profileId}`}
                className="BuildingLogModal__unitLink"
                target='_blank'
              >
                {item.profileName}
              </Link>
            ) : (
              item.profileName || 'neznámý'
            )} {item.unitId && item.unit && (
              <>
                do jednotky {' '}
                <Link
                  to={`${routes.units}unit/${item.unitId}`}
                  className="BuildingLogModal__unitLink"
                  target='_blank'
                >
                  {item.unit}
                </Link>
              </>
            )}
          </>
        );
      case 'removeProfile':
        return (
          <>
            Odebrán vlastník {item.profileId && item.profileName ? (
              <Link
                to={`${routes.units}profile/${item.profileId}`}
                className="BuildingLogModal__unitLink"
                target='_blank'
              >
                {item.profileName}
              </Link>
            ) : (
              item.profileName || 'neznámý'
            )} {item.unitId && item.unit && (
              <>
                z jednotky {' '}
                <Link
                  to={`${routes.units}unit/${item.unitId}`}
                  className="BuildingLogModal__unitLink"
                  target='_blank'
                >
                  {item.unit}
                </Link>
              </>
            )}
          </>
        );
      default:
        return `Akce ${item.actionType}`;
    }
  };
  
  return (
    <div key={`action-${index}`} className='BuildingLogModal__actionItem mb-1'>
      <Text size="body-medium" className='BuildingLogModal__listItem'>
        • {renderActionWithLinks()}
      </Text>
    </div>
  );
};

// Renderování celé tabulky s diff položkami
export const renderDiffItems = (diffItems: DiffItem[]): React.ReactNode => {
  if (diffItems.length === 0) {
    return (
      <Text size="body-medium" color="Neutral600">
        Detailní informace o změnách nejsou k dispozici.
      </Text>
    );
  }

  // Rozdělíme položky na jednotlivé typy
  const valueItems = diffItems.filter((item): item is ValueDiffItem => item.type === 'value');
  // Prázdné list/collection (bez přidaných i odebraných položek) nevykreslujeme — vznikaly
  // např. u nově sledovaných profilů bez záznamu v insolvenci a tvořily prázdné "Seznam" sekce.
  const listCollectionItems = diffItems.filter((item): item is ListCollectionDiffItem =>
    (item.type === 'list' || item.type === 'collection')
    && ((item.added?.length || 0) > 0 || (item.removed?.length || 0) > 0)
  );
  const actionItems = diffItems.filter((item): item is BuildingLogActionItem => item.type === 'action');
  const plombaItems = diffItems.filter((item): item is PlombaDiffItem => item.type === 'plomba');

  return (
    <>
      {/* Value položky v tabulce */}
      {valueItems.length > 0 && (
        <table className='BuildingLogModal__diffTable mb-2'>
          <thead>
            <tr>
              <th><Text size="body-small" weight="400" align="left">Položka</Text></th>
              <th><Text size="body-small" weight="400" align="left">Původní hodnota</Text></th>
              <th><Text size="body-small" weight="400" align="left">Nová hodnota</Text></th>
            </tr>
          </thead>
          <tbody>
            {valueItems.map((item, index) => renderValueDiffItem(item, index))}
          </tbody>
        </table>
      )}

      {/* List/Collection položky jako sekce */}
      {listCollectionItems.length > 0 && (
        <div className='BuildingLogModal__listSections'>
          {listCollectionItems.map((item, index) => renderListCollectionDiffItem(item, index))}
        </div>
      )}

      {/* Plomby (řízení v katastru) jako sekce */}
      {plombaItems.length > 0 && (
        <div className='BuildingLogModal__listSections'>
          {plombaItems.map((item, index) => renderPlombaItem(item, index))}
        </div>
      )}

      {/* Action položky jako seznam */}
      {actionItems.length > 0 && (
        <div className='BuildingLogModal__actionSections mb-2'>
          <Text size="h3" className='mb-1'>
            Provedené akce
          </Text>
          <div className='BuildingLogModal__actionItems'>
            {actionItems.map((item, index) => renderActionItem(item, index))}
          </div>
        </div>
      )}
    </>
  );
};
